import { opRequest } from '@/utils/request/opRequest';
import {
  goodDetailsResponse,
  skuInfoResponse,
  goodSearchResponse,
  goodSearchParams,
  brandDetailsResponse
} from '@/views/mall/opCompatibility';
import { request } from '@/utils/request';

// op 业务接口层
// ---------- 接口定义 ----------

//商品筛选搜索
export function opGoodsSearch(params) {
  return opRequest('/v1/goods/search', params);
  // return opRequest('/v1/goods/search', params).then(goodSearchResponse);
}
//分类列表
export function opCategoryIndex(params) {
  return opRequest('/v1/category/index', params);
}
//专场信息（含 channel_config 后端配置 / front_config 前端配置）
export function opChannelInfo(params) {
  return opRequest('/v1/channel/info', params);
}
// 首页入口菜单（移植 mall_h5 lib/optimize/apis 的 opHomeMenu：POST /v1/home/menu）
// 返回 { modules: [{ action, url, name, img|logo, ... }] }：
//   action = 'jump'    专场/频道入口，PC 直接 router.push(url)
//   action = 'ext_app' 扩展应用（点餐等），按 is_maintain / url 处理（见 SwitchChannel）
export function opHomeMenu(params = {}) {
  return opRequest('/v1/home/menu', params);
}
// 价格档位（PC 无对应封装，移植 mall_h5 lib/preview 的 goodsPriceGears：
// h5 的 searchService 与 PC 的 op 网关同为 newapi 网关，故直接走 opRequest）
export function goodsPriceGears(params) {
  return opRequest('/v1/goods/price/gears', params);
}
// 专场首页商品（PC 无对应封装，移植 mall_h5 lib/optimize/apis 的 opGoodsHomepage：
// 一次性返回分类 + 分类下商品，兑换专区「非分类配置」分支与 h5 一致用这个接口）
export function opGoodsHomepage(params) {
  return opRequest('/v1/goods/homepage', params);
}
// 品牌列表（移植 mall_h5 lib/optimize/apis 的 opReqBrandList：
// h5 用 sessionStorage.cityId，PC 传当前城市 id；返回项补 id = brand_id）
export function opReqBrandList(params = {}) {
  return opRequest('/v1/brand/list', params).then(res => {
    const list = (res && res.list) || [];
    list.forEach(item => {
      item.id = item.brand_id;
    });
    return { ...(res || {}), list };
  });
}
// 扩展应用/点餐入口（移植 mall_h5 lib/optimize/apis 的 opGetExtAppList）
export function opGetExtAppList(params = {}) {
  return opRequest('/v1/ext_app/list', {
    hideAddCommonParam: true,
    ...params
  }).then(res => {
    const list = (res && res.ext_app_list) || [];
    return list.map(item => ({ ...item, is_ext_app: 1 }));
  });
}
// 广告位（移植 mall_h5 lib/optimize/apis 的 opGetAdvertising）
export function opAdvertising(params) {
  return opRequest('/v2/ad/advertising', params);
}
// 广告位跳转链接（移植 mall_h5 lib/request 的 getAdvertisingUrl）
export function opAdvertisingUrl(params) {
  return request('/v2/ad/advertising_link', params);
}
// 商品详情
export function opGoodsDetails(params) {
  return opRequest('/v1/goods/details', params).then(goodDetailsResponse);
}
// 商品规格
export function opGoodsSpec(params) {
  return opRequest('/v1/goods/sku/details', params).then(skuInfoResponse);
}
// 库存检测
export function opThirdCheckStock(params) {
  return request('/v2/third/check/stock', params);
}
// 商品库存（移植 mall_h5 lib/optimize/apis 的 opGoodsStock）：
// 与 opThirdCheckStock 是同一个接口，这里补上 h5 的前置判断与返参收敛：
// product_source / third_sku_id 缺一不可，缺失时按可售处理；
// 返回 { stock, codes }（stock=false 表示该 sku 不可售）
export function opGoodsStock(params = {}) {
  return new Promise(resolve => {
    const {
      channel_id,
      address_id,
      product_source,
      third_sku_id,
      count = 1
    } = params;
    if (!product_source || !third_sku_id) return resolve({ stock: true });
    const newParams = {
      product_source,
      sku: [{ sku_id: third_sku_id, num: count }]
    };
    if (channel_id) newParams.channel_id = channel_id;
    if (address_id) newParams.address_id = address_id;
    opThirdCheckStock(newParams)
      .then(res => {
        // 兼容响应包裹：可能直接返回 {can_sale_sku,not_can_sale_sku} 或包在 data 中
        const payload = (res && (res.data || res)) || {};
        const notCanSale = payload.not_can_sale_sku || [];
        if (!notCanSale.length) {
          return resolve({ stock: true, codes: payload.codes });
        }
        resolve({
          stock: !(Number(notCanSale[0]) === Number(third_sku_id)),
          codes: payload.codes
        });
      })
      .catch(() => {
        // 检测接口异常时不阻断加购（与商品详情页 sku-panel 的取舍保持一致）
        resolve({ stock: true });
      });
  });
}

export function opOrderConfirm(params) {
  return request('/v2/order/confirm', params);
}

// 加入购物车
export function opGoodsCartAdd(params) {
  return opRequest('/v1/cart/add', params);
}

// 修改购物车数量 / 商品信息（移植 mall_h5 lib/optimize/apis 的 opCartUpDate）：
// 入参 { cart_id, sku_id, count, channel_id }
export function opCartUpDate(params = {}) {
  return opRequest('/v1/cart/update', params);
}

// 预购专场和用户验证
export function preOrderCheckUser(params) {
  return request(`/v2/pre/check/channel/user`, params);
}

// 获取购物车列表
export function opCartList(params) {
  return opRequest(`/v1/cart/list`, params);
}

// 获取购物车信息
export function opCartCount(params) {
  return opRequest(`/v1/cart/count`, params);
}

// 购物车结算
export function opCartSettlement(params) {
  return opRequest(`/v1/cart/settlement`, params);
}

// 结算时获取配送时间（移植 mall_h5 lib/optimize/apis 的 opGetDeliveryInfo）：
// 入参 { goods: 'goods_id1,goods_id2' }，
// 返回 [{ goods_id, delivery_times: [{ assign_date, is_choose, delivery_time: [{ start, end }] }] }]
// 失败不 reject：拿不到就当没有可预约时间，不阻塞结算（与 h5 一致）
export function opGetDeliveryInfo(params = {}) {
  return opRequest('/v1/goods/delivery/info', params).catch(() => []);
}

// 获取配件商品信息（移植 mall_h5 lib/order.js 的 goodsFittings）：
// 入参 { goods_id: [{ goods_id, provider_id }, ...] }，
// 返回 [{ parent_goods_id, fittings: [{ goods_id, sku_id, goods_name, goods_img,
//                                     shop_price, goods_number, ... }] }]
export function goodsFittings(params = {}) {
  return request('/v2/goods/fittings', params);
}

// 品牌门店列表（移植 mall_h5 lib/optimize/apis 的 opGetBrandStore）：
// h5 走 searchService（apiv2 网关），与 PC 的 op 网关一致；
// 入参 { brand_id, city_id, city_code, latitude, longitude }，
// 返回 { list: 门店数组, city_data: [{ city, city_code, store_count, is_checked }] }
export function opGetBrandStore(params = {}) {
  return opRequest('/v1/goods/store/list', params);
}

// 门店详情（移植 mall_h5 lib/canshihui 的 mealStoreDetails）：
// 品牌详情「按门店进入」时取该门店，返回 { store }
export function mealStoreDetails(params = {}) {
  return opRequest('/meal/service/store/details', params);
}

// 品牌详情（移植 mall_h5 lib/optimize/apis 的 opGetPdBrandDetail / lib/request 的 generalBrandDetails）：
// 入参 { brand_id, channel_id, city_id }；返参把 brand_details 平铺到顶层
// （含 brand_name / brand_logo / brand_banner / brand_describe / goods_types / cancel_brand_id）
export function opGetPdBrandDetail(params = {}) {
  return opRequest('/v1/brand/details', params).then(brandDetailsResponse);
}

// 品牌分类列表（移植 mall_h5 lib/optimize/apis 的 opGetCategoryList）：只取 category_list 数组
export function opGetCategoryList(params = {}) {
  return opRequest('/v1/category/index', params).then(
    res => (res && res.category_list) || []
  );
}

// 商品筛选搜索（移植 mall_h5 lib/request 的 findChannelGoods → lib/optimize/apis 的 opGoodSearch）：
// 入参统一化（goodSearchParams）+ 返参映射（goodSearchResponse）
// 返回 { items, lastPage/totalPage, page, total, filter.cate_list ... }
export function findChannelGoods(params = {}) {
  return opRequest('/v1/goods/search', goodSearchParams(params)).then(
    goodSearchResponse
  );
}
